import { cn } from '@/lib/utils';

export interface StatusTabOption {
  value: string;
  label: string;
  /** Optional count rendered next to the label. Pass `undefined` to hide. */
  count?: number;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: StatusTabOption[];
  className?: string;
}

/**
 * Linear-style clickable status tabs row that replaces a status dropdown
 * filter. Renders as a horizontal strip with each option as a small button;
 * the active option gets a bolder text + a 2px underline. Counts render in
 * a muted monospace pill so they read as data, not labels.
 */
export function StatusTabs({ value, onChange, options, className }: Props) {
  return (
    <div
      className={cn(
        'flex items-center gap-0.5 border-b border-border/60',
        className,
      )}
      role="tablist"
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              'relative flex items-center gap-1.5 px-3 py-2 text-[12.5px] transition-colors',
              active
                ? 'font-medium text-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <span>{opt.label}</span>
            {opt.count !== undefined && (
              <span
                className={cn(
                  'inline-flex h-4 min-w-[18px] items-center justify-center rounded px-1 text-[10.5px] font-medium tabular-nums',
                  active
                    ? 'bg-foreground/10 text-foreground'
                    : 'bg-muted text-muted-foreground/80',
                )}
              >
                {opt.count}
              </span>
            )}
            {active && (
              <span
                aria-hidden="true"
                className="absolute inset-x-2 -bottom-px h-[2px] rounded-full bg-foreground"
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
